import { PreIcon } from '@advanza/ui'
import clsx from 'clsx'
import React, {
    ChangeEvent,
    ComponentPropsWithoutRef,
    CSSProperties,
    PropsWithChildren,
    ReactNode,
} from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import style from './../inputField.module.css'

type Props = {
    after?: ReactNode
    afterPlaceholder?: ReactNode
    before?: ReactNode
    blueBorderValid?: boolean
    classNames?: string
    compact?: boolean
    controlled?: boolean
    defaultErrorMsg?: ReactNode
    disableValueFocus?: boolean
    error?: any
    hideAfterIconStyle?: boolean
    hidePlaceholder?: boolean
    inputClassName?: string
    inputPlaceholder?: ReactNode
    inputStyle?: CSSProperties
    labelClassName?: string
    legend?: boolean
    newStyle?: boolean
    placeholder?: ReactNode
    placeholderLarge?: string
    preIcon?: boolean
    ref?: any
    registerRef?: (el: HTMLInputElement | null) => void
    style?: CSSProperties
    textArea?: boolean
    value?: string | number | null
} & WithTranslation &
    Pick<
        ComponentPropsWithoutRef<'input'>,
        | 'autoComplete'
        | 'autoFocus'
        | 'disabled'
        | 'id'
        | 'inputMode'
        | 'max'
        | 'maxLength'
        | 'min'
        | 'name'
        | 'onBlur'
        | 'onChange'
        | 'onClick'
        | 'onFocus'
        | 'onKeyDown'
        | 'onKeyPress'
        | 'onKeyUp'
        | 'readOnly'
        | 'step'
        | 'title'
        | 'type'
    > &
    Pick<ComponentPropsWithoutRef<'textarea'>, 'rows'>

/**
 * @deprecated use @trustoo/components/input/InputField instead
 *
 * @doc See migration guide: docs/guidelines/input_field_migration_guide.md
 */
class InputField extends React.Component<
    PropsWithChildren<Props>,
    { value: string; focused: boolean }
> {
    hasAutofocused: number
    private ref: HTMLInputElement | null | undefined
    constructor(props: PropsWithChildren<Props>) {
        super(props)
        this.onFocus = this.onFocus.bind(this)
        this.onBlur = this.onBlur.bind(this)
        this.doFocus = this.doFocus.bind(this)
        this.state = { focused: false, value: '' }
        this.hasAutofocused = 0
    }

    onFocus() {
        const { readOnly } = this.props

        if (readOnly) {
            ;(document.activeElement as HTMLElement).blur()
        } else {
            this.setState({ focused: true })
        }
    }

    onBlur(e: React.FocusEvent<HTMLInputElement>) {
        this.setState({ focused: false })
        this.props.onBlur && this.props.onBlur(e)
    }

    inputPropWhiteList = [
        'autoComplete',
        'disabled',
        'id',
        'inputMode',
        'max',
        'maxLength',
        'min',
        'onClick',
        'onFocus',
        'onFocus',
        'onKeyDown',
        'onKeyPress',
        'onKeyUp',
        'readOnly',
        'rows',
        'step',
        'title',
    ]

    doFocus() {
        this.ref && this.ref.focus()
    }

    render() {
        const {
            id = '',
            t,
            hidePlaceholder,
            value: valueProp = '',
            error,
            blueBorderValid,
            compact,
            defaultErrorMsg,
            placeholder,
            afterPlaceholder,
            inputPlaceholder,
            placeholderLarge,
            textArea,
            onChange,
            before,
            name,
            type = 'text',
            readOnly = false,
            after,
            hideAfterIconStyle = false,
            children,
            style: inlineStyle,
            autoFocus,
            labelClassName,
            inputStyle,
            inputClassName,
            classNames = '',
            preIcon = false,
            legend = false,
            disableValueFocus = false,
            newStyle = false,
            controlled = true,
            ...rest
        } = this.props
        const value = (controlled ? valueProp : this.state.value) ?? ''
        const hasValue = value === 0 || (value && value !== '')
        const hasActiveFocus = this.state.focused
        const hasFocus = (hasActiveFocus && !rest.disabled) || hasValue
        const errorMsg = (() => {
            if (typeof error === 'string') {
                return error
            }
            if (defaultErrorMsg === undefined) {
                return t(['form_default_error_text', 'Check this field'])
            }
            return defaultErrorMsg || ''
        })()

        const props = {
            id,
            'data-field-has-error': error ? true : undefined,
            onFocus: this.onFocus,
            onBlur: this.onBlur,
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
                onChange && onChange(e)
                !controlled && this.setState({ value: e.target.value })
            },
            placeholder: inputPlaceholder,
            name,
            value,
            type,
            style: inputStyle,
            readOnly,
            className: clsx(style.input, inputClassName),
            ref: (el: HTMLInputElement | null) => {
                if (autoFocus && this.hasAutofocused < 5) {
                    el && document.activeElement !== el && el.focus()
                    this.hasAutofocused++
                }
                this.ref = el
                rest.ref && rest.ref(el)
                rest.registerRef && rest.registerRef(el)
            },
        }
        Object.keys(rest).forEach((key) => {
            if (this.inputPropWhiteList.includes(key)) {
                //@ts-expect-error
                props[key] = rest[key]
            }
        })
        const className = [
            style.root,
            hasActiveFocus ? style.activeFocus : '',
            hasValue && !(legend || disableValueFocus) ? style.valueFocus : '',
            hasFocus ? style.focused : '',
            hidePlaceholder ? style.hidePlaceholder : '',
            value === '' ? '' : style.notEmpty,
            error ? style.hasError : '',
            blueBorderValid ? style.validBorder : '',
            compact ? style.compact : '',
            legend ? style.hasLegend : '',
            newStyle ? style.newStyle : '',
        ].join(' ')
        return (
            <div className={[className, classNames].join(' ')} style={inlineStyle}>
                {legend && (
                    <div className={style.legend}>
                        {placeholder}
                        {afterPlaceholder}
                    </div>
                )}
                <label className={clsx(style.label, labelClassName)}>
                    {before && <div className={style.before}>{before}</div>}
                    {React.createElement(textArea ? 'textarea' : 'input', props)}
                    {!legend && (
                        <div className={style.placeholder}>
                            {hasFocus ? placeholder : placeholderLarge || placeholder}
                        </div>
                    )}
                    {children}
                    {after && (
                        <div
                            className={[
                                style.after,
                                hideAfterIconStyle ? '' : style.withIconStyle,
                            ].join(' ')}>
                            {after}
                        </div>
                    )}
                </label>
                {error && preIcon && (
                    <div className={style.errorPreIcon}>
                        <PreIcon name="error" fontSize={18}>
                            {errorMsg}
                        </PreIcon>
                    </div>
                )}
                {error && !preIcon && <div className={style.error}>{errorMsg}</div>}
            </div>
        )
    }
}

export default withTranslation('shared', { withRef: true })(InputField)
